import React from 'react';
import { Helmet } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PWAProvider } from './contexts/PWAContext';
import { ToastProvider } from './components/ui';
import { queryClient } from './lib/queryClient';
import ProtectedRoute from './components/auth/ProtectedRoute';
import GuardianProtectedRoute from './components/auth/GuardianProtectedRoute';
import { AppLayout, GuardianLayout } from './components/layout';
import MarketingLayout from './components/layout/MarketingLayout';
import DocsLayout from './components/layout/DocsLayout';
import DocsIndex from './pages/marketing/docs/DocsIndex';
import DocsSetup from './pages/marketing/docs/getting-started/Setup';
import DocsFirstObservation from './pages/marketing/docs/getting-started/FirstObservation';
import DocsPopia from './pages/marketing/docs/security/Popia';
import DocsDataResidency from './pages/marketing/docs/security/DataResidency';
import DocsDataExport from './pages/marketing/docs/security/DataExport';
import DocsBackups from './pages/marketing/docs/security/Backups';
import DocsBreachResponse from './pages/marketing/docs/security/BreachResponse';
import DocsCapsAlignment from './pages/marketing/docs/curriculum/CapsAlignment';
import DocsAiSuggestions from './pages/marketing/docs/curriculum/AiSuggestions';
import LegalTerms from './pages/marketing/legal/Terms';
import LegalSubprocessors from './pages/marketing/legal/Subprocessors';
import {
  LoginPage,
  Dashboard,
  Classrooms,
  ClassroomDetail,
  Curriculum,
  Children,
  ChildProfile,
  Observations,
  BulkObservations,
  Assessments,
  Reports,
  ThemeDemo,
  PWADemo,
  DevTools,
  ForgotPassword,
  ResetPassword,
  Users,
  UserDetail,
  Settings,
  SettingsBilling,
  AnalyticsDashboard,
  Notifications,
} from './pages';
import {
  HomePage,
  FeaturesPage,
  PricingPage,
  AboutPage,
  ContactPage,
} from './pages/marketing';
import {
  GuardianLogin,
  GuardianDashboard,
  GuardianChildren,
  GuardianChildDetail,
  GuardianObservations,
  GuardianAssessments,
  GuardianProfile,
  GuardianInvitation,
  GuardianPasswordReset,
  GuardianNotifications,
} from './pages/guardian';
import UIDemo from './pages/UIDemo';
import DevCredentials from './pages/DevCredentials';
import AdminOnboarding from './pages/AdminOnboarding';
import LeadManagement from './pages/admin/LeadManagement';
import LeadDetail from './pages/admin/LeadDetail';
import OrgManagement from './pages/admin/OrgManagement';
import { useAnalyticsPageView } from './hooks/useAnalytics';

/**
 * Rendered inside <Router> — emits a PostHog pageview on every route change.
 * Returns null so it doesn't affect layout.
 */
function AnalyticsPageViewTracker() {
  useAnalyticsPageView();
  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <PWAProvider>
          <AuthProvider>
            <ToastProvider>
              <Helmet>
                <title>Sprout — Educational Observation System</title>
                <meta name="description" content="Sprout is a CAPS-aligned observation and assessment platform for South African schools." />
              </Helmet>
              <Router>
            <AnalyticsPageViewTracker />
            <Routes>
              {/* Marketing / Public Routes with Marketing Layout */}
              <Route element={<MarketingLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                {/* Public docs section — nested DocsLayout adds the sidebar */}
                <Route element={<DocsLayout />}>
                  <Route path="/docs" element={<DocsIndex />} />
                  <Route path="/docs/getting-started/setup" element={<DocsSetup />} />
                  <Route path="/docs/getting-started/first-observation" element={<DocsFirstObservation />} />
                  <Route path="/docs/security/popia" element={<DocsPopia />} />
                  <Route path="/docs/security/data-residency" element={<DocsDataResidency />} />
                  <Route path="/docs/security/data-export" element={<DocsDataExport />} />
                  <Route path="/docs/security/backups" element={<DocsBackups />} />
                  <Route path="/docs/security/breach-response" element={<DocsBreachResponse />} />
                  <Route path="/docs/curriculum/caps-alignment" element={<DocsCapsAlignment />} />
                  <Route path="/docs/curriculum/ai-suggestions" element={<DocsAiSuggestions />} />
                </Route>
                {/* Legal — sits outside the DocsLayout sidebar; uses MarketingLayout chrome only */}
                <Route path="/legal/terms" element={<LegalTerms />} />
                <Route path="/legal/subprocessors" element={<LegalSubprocessors />} />
              </Route>

              {/* Auth Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Dev-only tools page. Public so logged-out devs can pick
                  an identity from the credentials tab. Hidden behind
                  import.meta.env.DEV inside DevTools so production builds
                  redirect away; PWA tab is additionally gated on
                  VITE_ENABLE_PWA (matching the old /pwa-demo route). */}
              <Route path="/dev" element={<DevTools />}>
                <Route index element={<Navigate to="credentials" replace />} />
                <Route path="credentials" element={<DevCredentials />} />
                <Route path="ui" element={<UIDemo />} />
                <Route path="theme" element={<ThemeDemo />} />
                <Route
                  path="pwa"
                  element={
                    import.meta.env.VITE_ENABLE_PWA === 'true'
                      ? <PWADemo />
                      : <Navigate to="/dev/credentials" replace />
                  }
                />
              </Route>

              {/* Guardian Public Routes */}
              <Route path="/guardian/login" element={<GuardianLogin />} />
              <Route path="/guardian/invitation" element={<GuardianInvitation />} />
              <Route path="/guardian/reset-password" element={<GuardianPasswordReset />} />

              {/* Guardian Protected Routes with Layout */}
              <Route
                path="/guardian"
                element={
                  <GuardianProtectedRoute>
                    <GuardianLayout />
                  </GuardianProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/guardian/dashboard" replace />} />
                <Route path="dashboard" element={<GuardianDashboard />} />
                <Route path="children" element={<GuardianChildren />} />
                <Route path="children/:childId" element={<GuardianChildDetail />} />
                <Route path="observations" element={<GuardianObservations />} />
                <Route path="assessments" element={<GuardianAssessments />} />
                <Route path="profile" element={<GuardianProfile />} />
                <Route path="notifications" element={<GuardianNotifications />} />
              </Route>

              {/* Protected Routes with App Layout */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
              </Route>
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="classrooms" element={<Classrooms />} />
                <Route path="classrooms/:classroomId" element={<ClassroomDetail />} />
                <Route path="curriculum" element={<Curriculum />} />
                <Route path="children" element={<Children />} />
                <Route path="children/:childId" element={<ChildProfile />} />
                <Route path="observations" element={<Observations />} />
                <Route path="bulk-observations" element={<BulkObservations />} />
                <Route path="assessments" element={<Assessments />} />
                <Route path="reports" element={<Reports />} />
                <Route
                  path="analytics"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AnalyticsDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route path="notifications" element={<Notifications />} />
                <Route
                  path="users"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="users/:userId"
                  element={
                    <ProtectedRoute requireAdmin>
                      <UserDetail />
                    </ProtectedRoute>
                  }
                />
                <Route path="settings" element={<Settings />} />
                <Route path="settings/billing" element={<SettingsBilling />} />
                <Route
                  path="admin/onboarding"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminOnboarding />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/orgs"
                  element={
                    <ProtectedRoute requireSuperAdmin>
                      <OrgManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/leads"
                  element={
                    <ProtectedRoute requireSuperAdmin>
                      <LeadManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/leads/:leadId"
                  element={
                    <ProtectedRoute requireSuperAdmin>
                      <LeadDetail />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Catch-all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
              </Router>
            </ToastProvider>
          </AuthProvider>
        </PWAProvider>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
